import React, { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send, ShieldAlert, ImagePlus } from "lucide-react";
import { toast } from "sonner";

export default function Chat({ requestId }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/requests/${requestId}/messages`);
      setMsgs(data);
    } catch (e) { console.warn("chat load failed", e); }
  };

  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [requestId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/requests/${requestId}/messages`, { content: text });
      setText("");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  const sendPhoto = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Solo imágenes");
    if (file.size > 10 * 1024 * 1024) return toast.error("Máx 10 MB");
    setSending(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/requests/${requestId}/messages/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  return (
    <div className="flex flex-col h-[420px]">
      <div className="flex-1 overflow-y-auto space-y-3 p-4 bg-[#FBF9F5] rounded-lg border border-[#0B1D3A]/10">
        {msgs.length === 0 && (
          <div className="text-center text-[#5C6B7D] text-sm py-16 font-display">
            Aún no hay mensajes. Coordina la visita, comparte fotos del barco y confirma el trabajo.
          </div>
        )}
        {msgs.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#0B1D3A] text-white" : "bg-white border border-[#0B1D3A]/10 text-[#0B1D3A]"}`}>
                {!mine && <div className="text-[10px] uppercase tracking-widest opacity-70 font-display mb-1">{m.sender_name}</div>}
                {m.image_path && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${m.image_path}`} target="_blank" rel="noreferrer">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${m.image_path}`}
                      className="rounded-lg max-w-full max-h-64 object-cover mb-1" alt="Foto compartida"/>
                  </a>
                )}
                {m.content && <div className="text-sm whitespace-pre-wrap">{m.content}</div>}
                {m.filtered && (
                  <div className={`mt-1.5 text-[10px] flex items-center gap-1 ${mine ? "text-white/70" : "text-[#FF6B57]"}`}>
                    <ShieldAlert size={11}/> Info de contacto bloqueada
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 mt-3">
        <label className="cursor-pointer inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#0B1D3A]/15 hover:bg-[#FBF9F5]" data-testid="chat-photo-label">
          <ImagePlus size={16} className="text-[#0B1D3A]"/>
          <input type="file" accept="image/*" className="hidden" onChange={(e) => sendPhoto(e.target.files?.[0])} data-testid="chat-photo-input"/>
        </label>
        <Input
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Escribe un mensaje (sin teléfonos ni emails)..."
          className="rounded-full border-[#0B1D3A]/15"
          data-testid="chat-input"
        />
        <Button onClick={send} disabled={sending} className="hub-btn-primary rounded-full gap-2" data-testid="chat-send-btn">
          <Send size={16}/> Enviar
        </Button>
      </div>
    </div>
  );
}
